import type { Block, Run } from './types'

/**
 * The subset of markdown a consent form needs, parsed into flat blocks.
 *
 * A direct port of `Markdown.kt`. The two must produce the same blocks for the
 * same input, because they are reading the same template files and are expected
 * to produce the same document.
 */

const HEADING = /^(#{1,6})\s+(.*)$/
const BULLET = /^\s*[-*]\s+(.*)$/
const NUMBERED = /^\s*(\d+)[.)]\s+(.*)$/
const RULE = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/
const NOTE = /^>\s?(.*)$/
const FIELD_LINE = /^\s*\{\{field:([A-Za-z0-9_]+)\}\}\s*$/
const SIG_LINE = /^\s*\{\{sig:([A-Za-z0-9_]+)\}\}\s*$/

export function parseMarkdown(body: string): Block[] {
  const blocks: Block[] = []
  let paragraph = ''

  const flush = () => {
    if (paragraph.trim()) blocks.push({ kind: 'paragraph', runs: parseInline(paragraph.trim()) })
    paragraph = ''
  }

  for (const raw of body.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n')) {
    const line = raw.replace(/\s+$/, '')

    if (!line.trim()) {
      flush()
      continue
    }
    // Checked before the heading rule so a row of underscores or asterisks is
    // never mistaken for anything else.
    if (RULE.test(line)) {
      flush()
      blocks.push({ kind: 'rule' })
      continue
    }
    const fieldLine = FIELD_LINE.exec(line)
    if (fieldLine) {
      flush()
      blocks.push({ kind: 'fieldLine', key: fieldLine[1]! })
      continue
    }
    const sigLine = SIG_LINE.exec(line)
    if (sigLine) {
      flush()
      blocks.push({ kind: 'signature', key: sigLine[1]! })
      continue
    }
    const heading = HEADING.exec(line)
    if (heading) {
      flush()
      blocks.push({ kind: 'heading', level: heading[1]!.length, runs: parseInline(heading[2]!.trim()) })
      continue
    }
    const note = NOTE.exec(line)
    if (note) {
      flush()
      blocks.push({ kind: 'note', runs: parseInline(note[1]!.trim()) })
      continue
    }
    const bullet = BULLET.exec(line)
    if (bullet) {
      flush()
      blocks.push({ kind: 'bullet', marker: '•', runs: parseInline(bullet[1]!.trim()) })
      continue
    }
    const numbered = NUMBERED.exec(line)
    if (numbered) {
      flush()
      blocks.push({ kind: 'bullet', marker: `${numbered[1]!}.`, runs: parseInline(numbered[2]!.trim()) })
      continue
    }
    // Soft-wrapped continuation of the paragraph being accumulated.
    paragraph = paragraph ? `${paragraph} ${line.trim()}` : line.trim()
  }
  flush()
  return blocks
}

const TOKEN = /\*\*|\*|\{\{[A-Za-z0-9_]+\}\}/g

/**
 * Splits a line into styled runs. `**` toggles bold, a lone `*` toggles italic,
 * and `{{key}}` becomes a placeholder run.
 *
 * An unbalanced marker stays literal rather than swallowing the rest of the
 * paragraph: a stray asterisk in legal boilerplate must never silently restyle
 * the clauses after it.
 */
export function parseInline(line: string): Run[] {
  const out: Run[] = []
  let buf = ''
  let bold = false
  let italic = false
  let i = 0

  const emit = () => {
    if (buf) {
      out.push({ text: buf, bold, italic })
      buf = ''
    }
  }

  TOKEN.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = TOKEN.exec(line)) !== null) {
    buf += line.slice(i, match.index)
    const token = match[0]
    const after = match.index + token.length

    if (token.startsWith('{{')) {
      emit()
      const key = token.slice(2, -2)
      out.push({ text: key, bold, italic, placeholder: key })
    } else if (token === '**') {
      // Only toggles if a partner exists later in the line.
      if (bold || line.indexOf('**', after) >= 0) {
        emit()
        bold = !bold
      } else {
        buf += token
      }
    } else if (italic || hasClosingItalic(line, after)) {
      emit()
      italic = !italic
    } else {
      buf += token
    }
    i = after
    TOKEN.lastIndex = after
  }
  buf += line.slice(i)
  emit()
  return out
}

/** A closing `*` that is not part of a `**` pair. */
function hasClosingItalic(line: string, from: number): boolean {
  let i = from
  while (i < line.length) {
    const at = line.indexOf('*', i)
    if (at < 0) return false
    if (line[at + 1] === '*') {
      i = at + 2
      continue
    }
    if (at > 0 && line[at - 1] === '*') {
      i = at + 1
      continue
    }
    return true
  }
  return false
}
